import React from 'react';
import { Link } from 'react-router-dom';

import { ButtonLink } from '@/components/ui/Button';
import { HeroSkeleton } from '@/components/ui/HeroSkeleton';
import { useFeaturedPostQuery } from '@/hooks/usePostsQueries';
import { formatDate } from '@/lib/formatDate';
import { captureMessage } from '@/lib/sentry';

export const FeaturedHero: React.FC = () => {
  const { data: post, isPending, error } = useFeaturedPostQuery();

  // While loading, show skeleton
  if (isPending) return <HeroSkeleton />;

  // If there's an error or no post, show graceful fallback (not debug UI)
  if (error || !post) {
    // Report to Sentry in production for monitoring
    if (error) {
      captureMessage('FeaturedHero: Failed to load featured post', 'warning', { error });
    } else {
      captureMessage('FeaturedHero: No featured post available', 'info');
    }

    // Graceful fallback - return null to hide the hero section entirely
    // This provides a cleaner UX than showing an error message
    return null;
  }

  return (
    <section className="bg-black">
      <div className="relative w-full h-[60vh] md:h-[70vh] overflow-hidden border-b border-neutral-800 group cursor-pointer shadow-industrial-lg">
        <div
          className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-smooth md:group-hover:scale-105 filter md:grayscale md:group-hover:grayscale-0"
          style={{ backgroundImage: post.thumbnailUrl ? `url(${post.thumbnailUrl})` : undefined }}
        />

        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent transition-opacity duration-300" />

        <div className="absolute inset-0 bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%),linear-gradient(90deg,rgba(255,0,0,0.06),rgba(0,255,0,0.02),rgba(0,0,255,0.06))] z-10 bg-[length:100%_2px,3px_100%] pointer-events-none" />

        <div className="absolute bottom-0 left-0 w-full pb-10 md:pb-12 z-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="max-w-4xl space-y-4">
              <div className="inline-block bg-neon-red text-black text-xs font-bold px-3 py-1.5 font-mono uppercase tracking-widest shadow-glow">
                KIEMELT {post.category ? `/ ${post.category}` : ''}
                {post.date && <span className="text-black/70"> • {formatDate(post.date)}</span>}
              </div>
              {/* Block-level link + roomy line-height so accented capitals (Á, É, Ő, Ű)
                  never collide with the badge above or the previous line */}
              <Link to={`/blog/${post.slug}`} className="block pt-1">
                <h2 className="text-3xl md:text-6xl font-sans font-bold text-white uppercase leading-tight md:leading-tight tracking-tighter drop-shadow-2xl md:group-hover:text-neon-red transition-colors duration-300 ease-smooth">
                  {post.title}
                </h2>
              </Link>
              {post.excerpt && (
                <p className="text-neutral-300 text-base md:text-xl font-body max-w-2xl line-clamp-2 md:line-clamp-3 transition-colors duration-300">
                  {post.excerpt}
                </p>
              )}

              <ButtonLink
                to={`/blog/${post.slug}`}
                variant="spotlight"
                className="mt-2"
                aria-label={`${post.title} cikk elolvasása`}
              >
                Olvasd tovább
              </ButtonLink>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
